import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/**
 * The tenants the signed-in user is allowed to order in.
 *
 * The orchestrator, reached through the `/platform-backend` proxy, is the only
 * source of this: the catalog carries a tenant on provisioned stacks but not on
 * the templates. A failed or empty lookup resolves to no tenants rather than an
 * error, because the answer is only used to name the tenant in a subtitle and a
 * decoration must not be able to take the page down with it.
 */
export const useAuthorizedTenants = (): string[] => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tenants, setTenants] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    discoveryApi
      .getBaseUrl('proxy')
      .then(baseUrl =>
        fetchApi.fetch(`${baseUrl}/platform-backend/api/v1/authorized-tenants`),
      )
      .then(async response => {
        if (!response.ok) throw new Error(await response.text());
        return (await response.json()) as string[];
      })
      .then(items => {
        // A body that is not a list is treated as no answer rather than trusted
        // into state, where `.length` on it would take the whole page down.
        if (active) setTenants(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (active) setTenants([]);
      });
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return tenants;
};
